import { VISIT_STATES, type VisitState } from '@servwell/shared';
import { VISIT_STATUS, display } from '../status';

/**
 * The tech day's status tabs (119, ref 23411385's Dispatched / Open / In-process / Completed).
 *
 * ⚠️ **Our words, not the reference's.** "Dispatched" and "In-process" are Commusoft's
 * vocabulary; ServWell's closed set is `VISIT_STATES`, rendered through `VISIT_STATUS` —
 * Scheduled / En route / In progress / Done / Canceled. Design-language §6.2 makes ONE
 * vocabulary per entity law, and a screen that invents a second one is how two parts of a
 * product start disagreeing about the same row.
 *
 * ⭐ **Derived from `VISIT_STATES`, never listed by hand.** A sixth visit state would appear
 * here automatically instead of silently having no tab — which would hide a whole class of
 * stop from the person standing next to it.
 */
export const ALL_TAB = 'all';

export interface DayTab {
  key: string;
  label: string;
  count: number;
}

/**
 * Every tab, with its count, in the order the day actually runs.
 *
 * ⚠️ **The set is FIXED, not derived from what today happens to contain.** A strip that
 * dropped its empty tabs would reflow under the thumb every time a tech tapped Start — the
 * status changes, a tab appears, and the control the finger was aiming at moves. Empty tabs
 * carry a `0` and an empty state instead, which is the honest version of the same
 * information and never moves.
 *
 * ⚠️ Counts come from the rows, so an empty tab says `0` rather than nothing. A tab with no
 * number beside it reads as "not loaded yet".
 */
export function dayTabs(rows: { visit: { status: string } }[]): DayTab[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    counts.set(r.visit.status, (counts.get(r.visit.status) ?? 0) + 1);
  }
  return [
    { key: ALL_TAB, label: 'All', count: rows.length },
    ...VISIT_STATES.map((s) => ({
      key: s,
      // The one vocabulary, read through the same helper every other surface uses.
      label: display(VISIT_STATUS, s).label,
      count: counts.get(s) ?? 0,
    })),
  ];
}

/**
 * The rows one tab shows.
 *
 * ⚠️ Returns the SAME array identity semantics as a filter — a caller sorting the result
 * must not mutate it in place. And `all` returns everything including canceled: a canceled
 * stop is exactly what a tech needs to see so they do not drive to it.
 */
export function rowsForTab<T extends { visit: { status: string } }>(
  rows: T[],
  tab: string,
): T[] {
  if (tab === ALL_TAB) return rows;
  return rows.filter((r) => r.visit.status === tab);
}

/** Whether a tab key is one this strip actually draws — junk falls back to All. */
export function isDayTab(tab: unknown): tab is string {
  return (
    tab === ALL_TAB || (VISIT_STATES as readonly string[]).includes(tab as VisitState)
  );
}
